import { useState, useCallback, useRef } from 'preact/hooks'
import type { ChatSession } from '../types'
import { listCloudSessions, loadCloudSession, checkCloudSessions } from '../services/api'

const PAGE_SIZE = 50

interface UseChatHistoryResult {
  sessions: ChatSession[]
  isLoading: boolean
  isLoadingMore: boolean
  hasMore: boolean
  error: string | null
  /** Refresh session list. Uses incremental check when possible. */
  refresh: (persona?: string) => Promise<void>
  /** Force a full reload (skip incremental check). */
  forceRefresh: (persona?: string) => Promise<void>
  /** Load the next page of older sessions and append. */
  loadMore: () => Promise<void>
  loadSession: (sessionId: string) => Promise<{
    session: ChatSession
    messages: Array<Record<string, string>>
  }>
  /** Add or update a session optimistically in the sidebar (e.g. active session with title). */
  upsertSession: (session: ChatSession) => void
}

export function useChatHistory(): UseChatHistoryResult {
  const [sessions, setSessions] = useState<ChatSession[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const lastSyncTsRef = useRef<string | null>(null)
  const lastPersonaRef = useRef<string | null>(null)
  const offsetRef = useRef(0)

  const doFullRefresh = useCallback(async (persona?: string) => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await listCloudSessions(persona, PAGE_SIZE, 0)
      const filtered = res.sessions.filter((s) => s.message_count === undefined || s.message_count > 0)
      setSessions(filtered)
      offsetRef.current = res.sessions.length
      setHasMore(res.sessions.length >= PAGE_SIZE)
      lastSyncTsRef.current = new Date().toISOString()
      lastPersonaRef.current = persona ?? null
    } catch (e) {
      setError(String(e))
    } finally {
      setIsLoading(false)
    }
  }, [])

  const refresh = useCallback(async (persona?: string) => {
    // Full refresh if persona changed or no prior sync
    if (!lastSyncTsRef.current || lastPersonaRef.current !== (persona ?? null)) {
      return doFullRefresh(persona)
    }

    // Incremental check
    try {
      const check = await checkCloudSessions(persona ?? '', lastSyncTsRef.current)
      if (check.up_to_date) return // Nothing changed

      // Something changed — do full refresh to get consistent list
      // (handles additions, updates, and deletions)
      return doFullRefresh(persona)
    } catch {
      // Check failed — fall back to full refresh
      return doFullRefresh(persona)
    }
  }, [doFullRefresh])

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return
    setIsLoadingMore(true)
    try {
      const persona = lastPersonaRef.current ?? undefined
      const res = await listCloudSessions(persona, PAGE_SIZE, offsetRef.current)
      const filtered = res.sessions.filter((s) => s.message_count === undefined || s.message_count > 0)
      setSessions((prev) => {
        // Dedupe by id in case of overlap with optimistic upserts.
        const seen = new Set(prev.map((s) => s.id))
        const additions = filtered.filter((s) => !seen.has(s.id))
        return [...prev, ...additions]
      })
      offsetRef.current += res.sessions.length
      setHasMore(res.sessions.length >= PAGE_SIZE)
    } catch (e) {
      setError(String(e))
    } finally {
      setIsLoadingMore(false)
    }
  }, [hasMore, isLoadingMore])

  const loadSession = useCallback(async (sessionId: string) => {
    return loadCloudSession(sessionId)
  }, [])

  const upsertSession = useCallback((session: ChatSession) => {
    setSessions((prev) => {
      const idx = prev.findIndex((s) => s.id === session.id)
      if (idx >= 0) {
        const updated = [...prev]
        updated[idx] = session
        return updated
      }
      return [session, ...prev]
    })
  }, [])

  return {
    sessions,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    refresh,
    forceRefresh: doFullRefresh,
    loadMore,
    loadSession,
    upsertSession,
  }
}
